"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import AppLogo from "@/components/ui/AppLogo";
import Icon from "@/components/ui/AppIcon";
import { useAuth } from "@/contexts/AuthContext";
import { createClient } from "@/lib/supabase/client";

const navLinks = [
  { href: '/homepage', label: '首頁 Home' },
  { href: '/products', label: '產品 Products' },
  { href: '/about', label: '關於我們 About' },
  { href: '/testimonials', label: '客戶評價 Reviews' },
  { href: '/comparison', label: '比較 Compare' },
  { href: '/faq', label: '常見問題 FAQ' },
  { href: '/contact', label: '聯絡 Contact' },
];

export default function Header() {
  const pathname = usePathname();
  const router = useRouter();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, loading, signOut } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const supabase = createClient();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!user) { setIsAdmin(false); return; }
    supabase?.from('user_profiles')?.select('is_admin')?.eq('id', user?.id)?.maybeSingle()?.then(({ data }) => setIsAdmin(!!data?.is_admin));
  }, [user]);

  const handleSignOut = async () => {
    try {
      await signOut();
      setUserMenuOpen(false);
      router?.push('/homepage');
      router?.refresh();
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  const userInitial = user?.user_metadata?.full_name?.charAt(0)?.toUpperCase() ||
    user?.email?.charAt(0)?.toUpperCase() || 'U';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-navy-sm border-b border-navy-100/60"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-8xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link href="/homepage" className="flex items-center gap-3 group">
          <AppLogo
            src="/assets/images/ChatGPT_Image_2026_2_12_09_54_36-1771749634557.png"
            size={52}
            className="transition-transform duration-300 group-hover:scale-105"
          />
          <div className="hidden sm:block">
            <div
              className={`font-heading font-black text-lg leading-tight tracking-tight transition-colors duration-300 ${
                scrolled ? "text-navy" : "text-white"
              }`}
            >
              <span style={{ color: "#FF8C00" }}>PC</span>{" "}
              <span className={scrolled ? "text-navy" : "text-white"}>CARGO</span>
            </div>
            <div
              className={`text-xs font-medium tracking-widest uppercase transition-colors duration-300 ${
                scrolled ? "text-text-muted" : "text-white/70"
              }`}
            >
              LIMITED
            </div>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8" suppressHydrationWarning>
          {navLinks?.map((link) => (
            <Link
              key={link?.href}
              href={link?.href}
              className={`nav-link text-sm font-semibold tracking-wide transition-colors duration-200 ${
                pathname === link?.href
                  ? scrolled
                    ? "text-orange active" :"text-yellow-brand active"
                  : scrolled
                  ? "text-text-mid hover:text-navy" :"text-white/90 hover:text-white"
              }`}
            >
              {link?.label}
            </Link>
          ))}
          <Link
            href="/bulk-orders"
            className={`font-bold text-sm transition-colors ${
              pathname === "/bulk-orders" ? "text-orange" : "text-navy hover:text-orange"
            }`}
          >
            Bulk Orders 大量採購
          </Link>
        </nav>

        {/* CTA + Auth */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://wa.me/85259773386"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-orange hover:bg-orange-dark text-white px-5 py-2.5 rounded-full text-sm font-bold transition-all duration-200 shadow-orange-glow hover:shadow-none pulse-glow"
          >
            <Icon name="ChatBubbleLeftRightIcon" size={16} variant="solid" />
            WhatsApp
          </a>

          {!loading && (
            <>
              {user ? (
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-200 ${
                      scrolled
                        ? 'bg-orange text-white hover:bg-orange-dark' :'bg-white/20 text-white hover:bg-white/30 border border-white/30'
                    }`}
                    aria-label="User menu"
                  >
                    {userInitial}
                  </button>
                  {userMenuOpen && (
                    <div className="absolute right-0 top-12 w-48 bg-white rounded-xl shadow-lg border border-navy-100/40 py-2 z-50">
                      <Link
                        href="/account"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-navy hover:bg-gray-50 transition-colors"
                      >
                        <Icon name="UserCircleIcon" size={16} />
                        我的帳戶 Account
                      </Link>
                      {isAdmin && (
                        <Link
                          href="/admin"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2 px-4 py-2.5 text-sm text-orange font-semibold hover:bg-orange/5 transition-colors"
                        >
                          <Icon name="Cog6ToothIcon" size={16} />
                          Admin Panel
                        </Link>
                      )}
                      <hr className="my-1 border-gray-100" />
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Icon name="ArrowRightOnRectangleIcon" size={16} />
                        登出 Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  href="/login"
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 border ${
                    scrolled
                      ? 'border-navy text-navy hover:bg-navy hover:text-white' :'border-white/50 text-white hover:bg-white/10'
                  }`}
                >
                  <Icon name="UserCircleIcon" size={15} />
                  登入 Login
                </Link>
              )}
            </>
          )}
        </div>

        {/* Mobile Hamburger */}
        <button
          className={`md:hidden p-2 rounded-lg transition-colors ${
            scrolled ? "text-navy" : "text-white"
          }`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <Icon name={menuOpen ? "XMarkIcon" : "Bars3Icon"} size={24} />
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-navy-dark border-t border-navy-light/30 px-6 py-6 flex flex-col gap-4">
          {navLinks?.map((link) => (
            <Link
              key={link?.href}
              href={link?.href}
              onClick={() => setMenuOpen(false)}
              className={`text-base font-semibold py-2 border-b border-navy-light/20 transition-colors ${
                pathname === link?.href ? "text-yellow-brand" : "text-white/80 hover:text-white"
              }`}
            >
              {link?.label}
            </Link>
          ))}
          <Link
            href="/bulk-orders"
            onClick={() => setMenuOpen(false)}
            className={`text-base font-semibold py-2 border-b border-navy-light/20 transition-colors ${
              pathname === "/bulk-orders" ? "text-yellow-brand" : "text-white/80 hover:text-white"
            }`}
          >
            Bulk Orders 大量採購
          </Link>

          {/* Mobile Auth */}
          {!loading && (
            user ? (
              <>
                <Link
                  href="/account"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 text-base font-semibold py-2 border-b border-navy-light/20 text-white/80 hover:text-white transition-colors"
                >
                  <Icon name="UserCircleIcon" size={18} />
                  我的帳戶 My Account
                </Link>
                {isAdmin && (
                  <Link
                    href="/admin"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 text-base font-semibold py-2 border-b border-navy-light/20 text-orange hover:text-orange/80 transition-colors"
                  >
                    <Icon name="Cog6ToothIcon" size={18} />
                    Admin Panel
                  </Link>
                )}
                <button
                  onClick={() => { handleSignOut(); setMenuOpen(false); }}
                  className="flex items-center gap-2 text-base font-semibold py-2 text-red-400 hover:text-red-300 transition-colors"
                >
                  <Icon name="ArrowRightOnRectangleIcon" size={18} />
                  登出 Logout
                </button>
              </>
            ) : (
              <Link
                href="/login"
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-center gap-2 mt-2 py-3 rounded-full border border-white/30 text-white font-bold text-sm hover:bg-white/10 transition-colors"
              >
                <Icon name="UserCircleIcon" size={16} />
                登入 / 註冊 Login / Register
              </Link>
            )
          )}

          <a
            href="https://wa.me/85259773386"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 flex items-center justify-center gap-2 bg-orange text-white py-3 rounded-full font-bold text-sm"
          >
            <Icon name="ChatBubbleLeftRightIcon" size={16} variant="solid" />
            WhatsApp: +852 5977 3386
          </a>
        </div>
      )}
    </header>
  );
}